// Text of the help pages at /help and /help/:topic (replacing the old Zendesk help center).
//
// Each page is a list of blocks:
//   { type: 'qa', q, a }           a question and its answer; these also go into the page's
//                                  FAQPage data for search engines, so keep q and a self-contained
//   { type: 'section', title, md } a bold lead-in followed by markdown (paragraphs, lists, links)
//   { type: 'table', head, rows }  a simple table
//   { type: 'md', md }             plain markdown
// Markdown links starting with '/' stay inside the site.

export const CONTACT_URL = 'https://support.tutulist.com/hc/en-us/requests/new';

const contactLine = lead => ({ type: 'md', md: `${lead} [Contact us](${CONTACT_URL}).` });

export const HELP_HOME = {
  title: 'Tutulist Help',
  metaTitle: 'Help center | Tutulist',
  description:
    'Answers about buying and selling new and used dancewear on Tutulist: fees, payouts, shipping, final sale, Buyer Protection and condition grades.',
  intro:
    'Tutulist is a marketplace for buying and selling new and used dancewear, costumes and dance shoes. Here are quick answers to the questions we hear most.',
  blocks: [
    {
      type: 'qa',
      q: 'Can I return something?',
      a:
        "All sales are final. If an item isn't as described, arrives damaged or never ships, Buyer Protection covers your return shipping and gives you a full refund. If it just doesn't fit, list it on Tutulist and pass it on to another dancer.",
    },
    {
      type: 'qa',
      q: 'What does it cost to sell?',
      a:
        'Listing is free. When an item sells, Tutulist keeps 20% of the item price, with a $2.85 minimum. Card processing is included.',
    },
    {
      type: 'qa',
      q: 'When do I get paid?',
      a:
        'As soon as the order is complete: when the buyer confirms it arrived, or automatically 7 days after you mark it shipped.',
    },
    {
      type: 'qa',
      q: 'How does shipping work?',
      a:
        "Most sellers ship with a prepaid Tutulist label that's ready as soon as the order is paid. Ship within 7 days; orders that haven't shipped after 14 days cancel and refund automatically.",
    },
    {
      type: 'qa',
      q: 'What do the condition grades mean?',
      a:
        'New, Like New, Good and Fair each have a set meaning. See the [condition guide](/help/condition-guide).',
    },
    contactLine('Still need help?'),
  ],
};

export const HELP_TOPICS = [
  {
    topic: 'buying',
    navLabel: 'Buying',
    title: 'How buying works',
    metaTitle: 'How buying works | Tutulist Help',
    description:
      'How to buy new and used dancewear on Tutulist: paying, buying from several sellers, making offers, messaging sellers and what happens after you buy.',
    blocks: [
      {
        type: 'section',
        title: 'Paying',
        md:
          'Pay by credit or debit card, Apple Pay or Google Pay. Payments are handled securely by Stripe; Tutulist never sees your full card number.',
      },
      {
        type: 'section',
        title: 'Buying from more than one seller',
        md:
          'Your cart can hold items from different sellers. Each seller ships separately, so you check out once per seller and each order has its own shipping.',
      },
      {
        type: 'section',
        title: 'Making an offer',
        md: [
          '- Tap "Make an Offer" on a listing. The seller has 24 hours to accept, decline or send a counteroffer. After 24 hours the offer expires.',
          '- Once your offer is accepted, you have 24 hours to pay at that price. You must start the checkout process through the offer page.',
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Messaging a seller',
        md:
          'To ask a seller a question before you buy, verify your phone number first. This keeps spam off Tutulist.',
      },
      {
        type: 'section',
        title: 'After you buy',
        md: [
          '1. The seller ships, usually within 7 days, and you get tracking on your order page.',
          "2. When it arrives, open the order and tap I've received my order.",
          "3. If something's wrong, tap Dispute order within 7 days of the seller marking it shipped. See [Final sale and Buyer Protection](/help/buyer-protection).",
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Sold items',
        md: 'Every item is one of a kind. Once it\'s sold, the listing says "This item has sold."',
      },
      contactLine('Questions about an order?'),
    ],
  },
  {
    topic: 'selling',
    navLabel: 'Selling',
    title: 'How selling works',
    metaTitle: 'How selling works | Tutulist Help',
    description:
      'How to sell your dancewear on Tutulist: getting set up, listing tips, offers, sales, renewing listings, vacation mode and canceling orders.',
    blocks: [
      {
        type: 'section',
        title: 'Getting set up',
        md: [
          '- Verify your email, then add payout details through Stripe so we can pay you.',
          "- Stripe asks for your bank details, date of birth and part of your Social Security number because US banking rules require it. Tutulist doesn't store this information.",
          '- Your shop lives at tutulist.com/u/your-username, so you can share it anywhere.',
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Listing an item',
        md: [
          '- Use good light. Your first photo is the one shoppers see while browsing.',
          '- Photograph any wear, stains or damage, and mention them in the description.',
          '- Include brand, size, color, material and measurements. Shoppers search for these.',
          '- Choose the right condition grade (see the [condition guide](/help/condition-guide)).',
          "- Add the package weight. It's needed for your shipping label (see the weight guide on the [Shipping](/help/shipping) page).",
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Offers',
        md:
          'You have 24 hours to accept, decline or counter an offer. After 24 hours an unanswered offer expires.',
      },
      {
        type: 'section',
        title: 'Running a sale',
        md:
          "Put your whole shop or picked items on sale for 3, 7 or 14 days, at 10% to 50% off. An item can go on sale once it's been listed for 7 days without a price increase. Shoppers see the original price crossed out.",
      },
      {
        type: 'section',
        title: 'Renewing listings',
        md:
          'Listings older than 30 days can be renewed to move them back up in "newest" searches, up to 3 a day.',
      },
      {
        type: 'section',
        title: 'Vacation mode',
        md:
          "Going away? Turn on vacation mode in your dashboard. Your listings are hidden from search and can't be bought, and offers and messages pause until you're back.",
      },
      {
        type: 'section',
        title: 'Canceling an order',
        md:
          "If you can't ship an order, cancel it from the order page before you ship. Choose a reason, and the buyer is refunded in full. Please don't leave orders unshipped: after 14 days they cancel automatically.",
      },
      contactLine('Need help with your shop?'),
    ],
  },
  {
    topic: 'fees-and-payouts',
    navLabel: 'Fees and payouts',
    title: 'Fees and payouts',
    metaTitle: 'Selling fees and payouts | Tutulist Help',
    description:
      'Tutulist keeps 20% of the item price (minimum $2.85), with card processing included. See when payouts are sent and how the Tutulist wallet works.',
    blocks: [
      {
        type: 'section',
        title: 'What it costs to sell',
        md:
          "Listing is free. When an item sells, Tutulist keeps 20% of the item price, with a minimum of $2.85. Card processing is included, and shipping isn't counted in the fee.",
      },
      {
        type: 'table',
        head: ['Item sells for', 'Tutulist fee', 'You earn'],
        rows: [
          ['$10.00', '$2.85 (minimum)', '$7.15'],
          ['$25.00', '$5.00', '$20.00'],
          ['$60.00', '$12.00', '$48.00'],
        ],
      },
      {
        type: 'section',
        title: 'When you get paid',
        md: [
          '1. The buyer pays at checkout, and we hold the payment while the order is on its way.',
          "2. The order completes when the buyer taps I've received my order, or automatically 7 days after you mark it shipped.",
          "3. Your payout is sent to your bank as soon as the order is complete. You can follow each order's payment in your dashboard under Payouts.",
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Your Tutulist wallet',
        md:
          'If you sell before adding payout details, your earnings are kept safe in your Tutulist wallet. Add your payout details through Stripe, then tap Withdraw balance to send the full amount to your bank.',
      },
      {
        type: 'section',
        title: 'If a payout fails',
        md: `A payout usually fails because the bank details are wrong. The money goes back to your Stripe balance. Update your bank details in your payout settings and [contact us](${CONTACT_URL}) so we can resend it.`,
      },
      {
        type: 'section',
        title: 'Offering free shipping and labels',
        md:
          "If you offer free shipping and use a Tutulist label, the label cost comes out of that sale's earnings.",
      },
    ],
  },
  {
    topic: 'shipping',
    navLabel: 'Shipping',
    title: 'Shipping',
    metaTitle: 'Shipping and package weights | Tutulist Help',
    description:
      'How shipping works on Tutulist: prepaid labels, who pays, the 7-day ship-by deadline and a package weight guide for leotards, skirts, tights and warm-ups.',
    blocks: [
      {
        type: 'section',
        title: 'Two ways to ship',
        md: [
          "- Ship with Tutulist (recommended): a prepaid label is created as soon as the order is paid. Print it from the order page, or show the QR code at the post office if you don't have a printer.",
          '- Ship it yourself: set your own shipping price, then add the carrier and tracking number when you mark the order shipped.',
          '',
          'Choose in your dashboard under Shipping details.',
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Who pays for shipping',
        md:
          'You decide: the buyer pays, or you offer free shipping on every order or on orders over an amount you set. With free shipping, the label cost comes out of your earnings.',
      },
      {
        type: 'section',
        title: 'Ship-by deadline',
        md: [
          "- Please ship within 7 days of the order. We'll send reminders if it hasn't shipped.",
          "- Orders the carrier hasn't scanned after 14 days cancel automatically, and the buyer gets a full refund.",
          "- Once it's dropped off, open the order and tap I've shipped it. Your 7-day payout timer starts then.",
        ].join('\n'),
      },
      {
        type: 'section',
        id: 'package-weight',
        title: 'Package weight',
        md:
          'Every listing needs a weight so we can price the label. Weigh the item if you can, or use this guide. When in doubt, round up an ounce or two, and add an ounce or two for sizes L–XXL. Below is an estimated weight chart. Please note you are responsible for entering the correct weight on your listing.',
      },
      {
        type: 'table',
        head: ['Item', 'Description', 'Weight'],
        rows: [
          ['Leotard', 'Camisole or short sleeve, lightweight Lycra', '3 oz'],
          ['Leotard', 'Camisole or short sleeve, heavy fabric or cotton', '4.5 oz'],
          ['Leotard', 'Long sleeve, lightweight Lycra', '5.5 oz'],
          ['Leotard', 'Long sleeve, heavy fabric or cotton', '8 oz'],
          ['Skirt', 'Lightweight chiffon wrap skirt', '1.5 oz'],
          ['Skirt', 'Medium-weight Lycra wrap skirt', '2.5 oz'],
          ['Skirt', 'Pull-on skirt', '3.5 oz'],
          ['Tights', 'Boxed tights', '4 oz'],
          ['Warm-ups', 'Trash bag / ripstop shorts, light', '2 oz'],
          ['Warm-ups', 'Trash bag / ripstop shorts, thick', '6 oz'],
          ['Warm-ups', 'Trash bag / ripstop pants, light', '3.5 oz'],
          ['Warm-ups', 'Wrap sweater', '8 oz'],
          ['Warm-ups', 'Knit overalls, light (like tights)', '7 oz'],
          ['Warm-ups', 'Knit overalls, medium to thick', '14 oz'],
          ['Warm-ups', 'Long or full leg warmers', '7 oz'],
          ['Warm-ups', 'Short or medium leg warmers', '5 oz'],
        ],
      },
      {
        type: 'section',
        title: 'Shipping outside the US',
        md:
          'Tutulist ships within the US. Sellers can choose to accept international orders in their shipping settings.',
      },
      {
        type: 'md',
        md: `Label problem? Use Retry label on the order page, or [contact us](${CONTACT_URL}).`,
      },
    ],
  },
  {
    topic: 'buyer-protection',
    navLabel: 'Final sale and Buyer Protection',
    title: 'Final sale and Buyer Protection',
    metaTitle: 'Final sale and Buyer Protection | Tutulist Help',
    description:
      "All sales on Tutulist are final. Buyer Protection refunds you in full if an item isn't as described, arrives damaged or never ships.",
    blocks: [
      {
        type: 'section',
        title: 'All sales are final',
        md:
          "Many items are used or one of a kind, so Tutulist doesn't accept returns for fit, style or a change of mind. Read the description, look closely at the photos and ask the seller questions before you buy. If something doesn't fit, list it on Tutulist and pass it on to another dancer.",
      },
      {
        type: 'section',
        title: 'What Buyer Protection covers',
        md: [
          'You get a full refund, including shipping, if your item:',
          '',
          "- isn't as described (for example, listed as size M but it's a size 6, or has stains or tears the listing didn't show)",
          '- arrives damaged',
          '- never ships',
          '',
          "Buyer Protection is free and automatic for every purchase paid on Tutulist. It doesn't cover purchases made outside Tutulist, or wear and flaws that were shown or described in the listing.",
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'How to report a problem',
        md: [
          '1. Open your order and tap Dispute order within 7 days of the seller marking it shipped.',
          "2. Tell us what's wrong and add photos.",
          "3. We review it and get back to you. If the item wasn't accurately described, we refund you in full.",
          '',
          `If your package arrives after the order has already completed, you're still covered. We accept disputes for up to 5 days after the package was delivered. [Contact us](${CONTACT_URL}) as soon as it arrives.`,
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Canceled orders and refunds',
        md:
          "If a seller cancels, or doesn't ship within 14 days, you're refunded in full automatically. Refunds go back to your original payment method and usually show up within 5–10 business days, depending on your bank.",
      },
      {
        type: 'section',
        title: 'Can I cancel my order?',
        md:
          "Once an order is paid, you can't cancel it yourself. Message the seller right away; if they haven't shipped yet, they can cancel and refund you.",
      },
      {
        type: 'section',
        title: 'For sellers',
        md:
          "Describe and photograph every flaw. If you notice a problem before shipping, message the buyer and offer to cancel. If an item is found not as described, the buyer is refunded and the sale isn't paid out.",
      },
    ],
  },
  {
    topic: 'condition-guide',
    navLabel: 'Condition guide',
    title: 'Condition guide',
    metaTitle: 'Condition guide: New, Like New, Good, Fair | Tutulist Help',
    description:
      'What the New, Like New, Good and Fair condition grades mean for dancewear on Tutulist.',
    blocks: [
      {
        type: 'md',
        md:
          'Every listing has one of four condition grades. Sellers, choose the grade that honestly fits; buyers, use it alongside the photos and description.',
      },
      {
        type: 'table',
        head: ['Grade', 'What it means'],
        rows: [
          ['New', 'Never worn, with the original tags still attached.'],
          [
            'Like New',
            'No signs of wear on the inside or outside of the garment. Use this for new, never-worn items without tags.',
          ],
          ['Good', 'Minimal to no signs of wear on the outside. The tag may be worn or cut off.'],
          [
            'Fair',
            'Clean, with some minimal wear. Usually a simple design, such as spaghetti straps and no embellishment. Tags may be cut off.',
          ],
        ],
      },
    ],
  },
  {
    topic: 'community-guidelines',
    navLabel: 'Community guidelines',
    title: 'Community guidelines',
    metaTitle: 'Community guidelines | Tutulist Help',
    description:
      'The rules for buying, selling and messaging on Tutulist, and how to keep yourself and your account safe.',
    blocks: [
      {
        type: 'md',
        md:
          'Tutulist connects dancers through dancewear. We want everyone here to feel safe and comfortable, so these are the rules for buying, selling and messaging on Tutulist.',
      },
      {
        type: 'section',
        title: 'No abuse, bullying or harassment',
        md: [
          "We don't allow anyone to use Tutulist to embarrass, humiliate, hurt or threaten another person. That includes:",
          '',
          '- rude, aggressive or nasty comments and messages',
          "- unwelcome comments on someone's race, gender, sexuality, religion, body weight, shape, size, disability or any other part of who they are",
          '- sexualized language or sexually inappropriate behavior (zero tolerance)',
          '- repeated, unwanted messages, especially after someone has asked you to stop',
          "- threats, including threats to share someone's personal information or go to their address",
          '- encouraging others to bully, harass or threaten someone',
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'No hate speech or discrimination',
        md:
          "We have zero tolerance for hateful language or symbols about anyone's race, religion, sexuality, gender identity, nationality or disability, for refusing to buy from or sell to someone because of who they are, and for supporting hate groups or violence.",
      },
      {
        type: 'section',
        title: 'Keep it on Tutulist',
        md: [
          '- Buy, sell and message only on Tutulist. Never meet someone from Tutulist in person.',
          "- Don't share personal information like your phone number, address or email.",
          "- Always pay through Tutulist checkout. That's what Buyer Protection covers: items that aren't as described, don't arrive, or arrive with part of the order missing. Payments made any other way can't be protected.",
          `- Buying or selling a Tutulist listing outside Tutulist checkout breaks our terms, and members who do it may lose buying and selling privileges. If someone asks you to, [contact us](${CONTACT_URL}).`,
          '- Tutulist will never ask you to click a link to verify payment information in messages. If you receive a message on the platform asking for information, please reach out to Tutulist to confirm.',
        ].join('\n'),
      },
      {
        type: 'section',
        title: 'Protect your account',
        md:
          'Use a strong, unique password and watch for suspicious emails. Tutulist will never email you to ask for your password, and never asks for personal or payment information outside the site.',
      },
      {
        type: 'section',
        title: 'Report a problem',
        md: `If you see or experience harmful behavior, tell us through the [contact form](${CONTACT_URL}) or at hello@tutulist.com. We've got your back.`,
      },
    ],
  },
];

export const findHelpTopic = topic => HELP_TOPICS.find(t => t.topic === topic) || null;
